import { memo, useCallback, useEffect, useRef, useState } from 'react'
import { Input } from '@baseV2'

// v2 Input UI with the v1 Input contract (onChange(value), setValidity, errorMap, uppercase),
// so the profile forms keep their existing state and validation logic untouched.
const ProfileInput = ({ value, onChange, setValidity, errorMap, uppercase, ...props }) => {
	const elem = useRef()
	const isDirty = useRef(false)
	const [error, setError] = useState()

	const validate = useCallback(
		(elem) => {
			if ((!isDirty.current && elem.required) || (!value && !elem.required)) {
				setError(null)
				typeof setValidity === 'function' && setValidity(true)
				return
			}

			const validityState = elem.validity
			const isElementValid = validityState.valid

			if (isElementValid) {
				setError(false)
			} else if (validityState.valueMissing) {
				setError(errorMap?.valueMissing ?? 'Input is required')
			} else if (validityState.patternMismatch) {
				setError(errorMap?.patternMismatch ?? 'Input does not match the criteria')
			} else if (validityState.tooLong) {
				setError(errorMap?.tooLong ?? 'Value is too long')
			} else if (validityState.tooShort) {
				setError(errorMap?.tooShort ?? 'Value is too short')
			} else if (validityState.typeMismatch) {
				setError(errorMap?.typeMismatch ?? 'Value is not of correct type')
			} else {
				setError('Input is invalid')
			}

			typeof setValidity === 'function' && setValidity(isElementValid)
		},
		[errorMap, setValidity, value]
	)

	useEffect(() => {
		if (elem.current) {
			validate(elem.current)
		}
	}, [value])

	const changeHandler = (e) => {
		if (typeof onChange !== 'function') return

		let value = e.target.value ?? ''
		if (uppercase) {
			value = value.toUpperCase()
		}
		if (props.type === 'number') {
			if (value === '') {
				onChange(value)
			} else if (props.allowNegative) {
				onChange(value)
			} else {
				onChange(Math.abs(value))
			}
		} else if (props.type === 'tel') {
			const re = /^[0-9\b]+$/
			if (value === '' || re.test(value)) {
				onChange(value.trim())
			}
		} else onChange(value)
	}

	const focusHandler = (e) => {
		if (e?.type === 'focus' && !isDirty.current) {
			isDirty.current = true
		}
	}

	return (
		<Input
			ref={elem}
			value={value ?? ''}
			onChange={changeHandler}
			onFocus={focusHandler}
			onBlur={focusHandler}
			error={error || ''}
			{...props}
		/>
	)
}

export default memo(ProfileInput)
